import React, { useState, useEffect } from "react";
import api from "../services/api";
import { Brain, Sparkles, Zap, Plus, X, Loader2, Star, HelpCircle, BookOpen } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import { getToken } from "../services/auth";
import toast, { Toaster } from 'react-hot-toast';

export default function AIRevision() {
  const [userRole, setUserRole] = useState("student");
  const [weakTopics, setWeakTopics] = useState([]);
  const [newTopic, setNewTopic] = useState("");
  const [loading, setLoading] = useState(true);
  const [boosterData, setBoosterData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      const token = getToken();
      if (token) setUserRole(jwtDecode(token).role || "student");
    } catch (err) {}
    loadPlan();
  }, []);

  const loadPlan = async () => {
    setLoading(true);
    try {
      const res = await api.get("/personal-plan/");
      setWeakTopics(res.data.weak_topics || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load study profile!");
    } finally {
      setLoading(false);
    }
  };

  const addTopic = async () => {
    if (!newTopic.trim()) return;
    const updated = [...weakTopics, newTopic.trim()];
    setWeakTopics(updated);
    setNewTopic("");
    await savePlan(updated);
  };

  const removeTopic = async (idx) => {
    const updated = weakTopics.filter((_, i) => i !== idx);
    setWeakTopics(updated);
    await savePlan(updated);
  };

  const savePlan = async (updatedList) => {
    setSaving(true);
    try {
      await api.put("/personal-plan/", { weak_topics: updatedList });
      toast.success("Revision matrix updated!");
    } catch (err) {
      toast.error("Couldn't sync topics.");
    } finally {
      setSaving(false);
    }
  };

  const triggerBooster = async () => {
    if (weakTopics.length === 0) {
      toast.error("Add at least one weak topic first!");
      return;
    }

    setGenerating(true);
    try {
      const res = await api.post("/ai/revision-boost", { weak_topics: weakTopics });
      setBoosterData(res.data);
      toast.success("AI Smart Study Sheet Generated! ⚡");
    } catch (err) {
      toast.error("AI revision module overloaded. Try again!");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex h-screen bg-[#FBFDFF] dark:bg-slate-950 overflow-hidden relative font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <main className="flex-1 flex flex-col h-full relative overflow-y-auto pb-24 z-10 min-w-0">
        
        {/* Visual backing glow */}
        <div className="absolute top-[-10%] right-[-10%] w-[35vw] h-[35vw] bg-sky-400/10 rounded-full blur-[110px] pointer-events-none animate-pulse" />
        <div className="absolute bottom-[-5%] left-[-5%] w-[30vw] h-[30vw] bg-indigo-400/10 rounded-full blur-[100px] pointer-events-none" />

        {/* Top Nav */}
        <header className="h-24 px-8 flex items-center justify-between bg-white/40 dark:bg-slate-900/40 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 relative z-20 flex-shrink-0">
          <div>
            <h1 className="text-2xl font-black text-indigo-950 dark:text-indigo-400 tracking-tight flex items-center gap-2">
              Smart Revision Hub <Brain size={22} className="text-indigo-600 fill-indigo-100" />
            </h1>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-0.5">Convert learning vulnerabilities into cognitive strengths.</p>
          </div>
        </header>

        <div className="flex-1 p-6 md:p-10 max-w-6xl mx-auto w-full space-y-10">
          
          {/* Setup Container */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Topics Management */}
            <motion.div 
              initial={{opacity: 0, x: -20}}
              animate={{opacity: 1, x: 0}}
              className="lg:col-span-5 bg-white dark:bg-slate-900 p-8 rounded-[3rem] border-b-[12px] border-slate-100 dark:border-slate-800 shadow-xl relative overflow-hidden"
            >
              <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2">Weak Topics Tracker 🚨</h3>
              <p className="text-xs font-bold text-slate-400 mb-6">List the subjects you find tricky. The AI will build hacks for them.</p>

              <div className="space-y-4">
                {/* Input Add */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Add a tricky topic (e.g., Algebra, Mitochondria)"
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addTopic()}
                    className="w-full bg-[#F6F8FF] dark:bg-slate-800 font-bold text-sm border-2 border-slate-50 dark:border-slate-800 rounded-2xl px-5 py-4 pr-14 focus:outline-none focus:border-indigo-300 focus:bg-white transition-all"
                  />
                  <button 
                    onClick={addTopic}
                    className="absolute right-2.5 top-2.5 p-2.5 bg-indigo-600 text-white rounded-xl border-b-4 border-indigo-800 shadow-md hover:bg-indigo-700 transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>

                {/* Chip Array */}
                <div className="min-h-[150px] py-4 flex flex-wrap gap-3 items-start border-t border-dashed border-slate-200 dark:border-slate-700 mt-4">
                  {loading ? (
                    <div className="flex gap-2 items-center font-bold text-slate-400 text-xs animate-pulse"><Loader2 className="animate-spin" size={14} /> Reading database...</div>
                  ) : weakTopics.length === 0 ? (
                    <p className="text-xs font-bold text-slate-400 italic py-4">No weak topics reported! Add some above to generate AI Study sheets.</p>
                  ) : (
                    <AnimatePresence>
                      {weakTopics.map((topic, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.8, opacity: 0 }}
                          className="px-4 py-2.5 bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 text-xs font-black rounded-xl border-2 border-red-100 dark:border-red-900 flex items-center gap-3 shadow-sm"
                        >
                          {topic}
                          <button onClick={() => removeTopic(idx)} className="hover:text-red-800 transition-colors"><X size={12} /></button>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  )}
                </div>

                <button
                  onClick={triggerBooster}
                  disabled={generating || weakTopics.length === 0}
                  className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-black py-4 rounded-2xl border-b-[8px] border-indigo-800 shadow-lg flex items-center justify-center gap-2 active:translate-y-1 active:border-b-0 disabled:opacity-50 transition-all mt-4"
                >
                  {generating ? <Loader2 className="animate-spin" /> : <Sparkles size={18} fill="currentColor" />}
                  Launch Smart Revision AI
                </button>
              </div>
            </motion.div>

            {/* AI Generated Workspace */}
            <div className="lg:col-span-7">
              <AnimatePresence mode="wait">
                {generating ? (
                  <motion.div 
                    key="gen" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
                    className="h-full min-h-[400px] bg-white dark:bg-slate-900 rounded-[3rem] border-4 border-dashed border-slate-200 dark:border-slate-800 flex flex-col items-center justify-center p-10 text-center"
                  >
                    <motion.div animate={{scale:[1,1.2,1]}} transition={{repeat:Infinity, duration: 1.5}} className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                      <Brain className="text-indigo-600" size={30} />
                    </motion.div>
                    <h4 className="text-xl font-black text-slate-700 dark:text-slate-200">Compiling Memory Pathways...</h4>
                    <p className="text-sm font-bold text-slate-400 mt-2 max-w-sm">Our AI cognitive coach is mapping Mnemonics, simplified rules, and short quizzes for your custom sheet.</p>
                  </motion.div>
                ) : !boosterData ? (
                  <motion.div 
                    key="idle" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
                    className="h-full min-h-[400px] bg-slate-50 dark:bg-slate-900/50 rounded-[3rem] border-4 border-dashed border-slate-200 dark:border-slate-800 flex flex-col items-center justify-center p-10 text-center relative group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[3rem]" />
                    <div className="relative flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center shadow-md mb-4">
                        <Zap className="text-slate-300 group-hover:text-indigo-500 transition-colors" size={30} fill="currentColor" />
                      </div>
                      <h4 className="text-lg font-black text-slate-500 dark:text-slate-300">Smart Study Sheet Awaits</h4>
                      <p className="text-xs font-bold text-slate-400 mt-2 max-w-xs">Define your weaknesses on the left, then trigger the engine to create visually encoded study guides.</p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div 
                    key="data" initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} exit={{opacity:0}}
                    className="space-y-6"
                  >
                    {/* Coaching Header Card */}
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 rounded-[3rem] border-b-[10px] border-indigo-800 text-white shadow-xl relative overflow-hidden">
                      <div className="absolute inset-0 bg-[url('https://transparenttextures.com/patterns/skulls-and-crossbones.png')] opacity-10" />
                      <div className="relative z-10 flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-xl shrink-0 border border-white/30">🧭</div>
                        <div>
                          <p className="text-[10px] uppercase font-black tracking-widest text-white/70">Message from Coach</p>
                          <p className="text-lg font-black leading-relaxed tracking-tight mt-1">"{boosterData.coaching_intro}"</p>
                        </div>
                      </div>
                    </div>

                    {/* Breakdown Loop */}
                    {boosterData.topic_breakdowns?.map((b, i) => (
                      <motion.div
                        key={i}
                        initial={{opacity:0, y:15}}
                        animate={{opacity:1, y:0}}
                        transition={{delay: i * 0.1}}
                        className="bg-white dark:bg-slate-900 border-b-8 border-slate-100 dark:border-slate-800 p-6 rounded-[2.5rem] shadow-xl relative group"
                      >
                        <div className="flex items-center gap-3 mb-4 border-b pb-3 border-slate-50 dark:border-slate-800">
                          <span className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-black shadow-sm">#{i+1}</span>
                          <h4 className="text-lg font-black text-slate-800 dark:text-white tracking-tight uppercase">{b.topic}</h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-2xl">
                            <p className="text-[10px] font-black text-indigo-600 uppercase flex items-center gap-1 mb-2 tracking-widest"><BookOpen size={12}/> Core Fact</p>
                            <p className="text-xs font-bold leading-relaxed text-slate-600 dark:text-slate-300">{b.simple_core_rule}</p>
                          </div>
                          
                          <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-2xl border border-amber-100 dark:border-amber-900">
                            <p className="text-[10px] font-black text-amber-600 uppercase flex items-center gap-1 mb-2 tracking-widest"><Star size={12} fill="currentColor"/> Mnemonic / Hack</p>
                            <p className="text-xs font-black leading-relaxed text-amber-900 dark:text-amber-400">{b.memory_trick}</p>
                          </div>

                          <div className="bg-emerald-50 dark:bg-emerald-950/30 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-900">
                            <p className="text-[10px] font-black text-emerald-600 uppercase flex items-center gap-1 mb-2 tracking-widest"><HelpCircle size={12}/> Active Recall</p>
                            <p className="text-xs font-bold italic leading-relaxed text-emerald-900 dark:text-emerald-400">"{b.quick_quiz}"</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>

        </div>
      </main>
    </div>
  );
}
